/**
 * Singleton composable for the daily review assignments page (/reviews-home).
 *
 * === ARCHITECTURE ===
 *
 * This composable holds ALL shared state for the assignments page. Three components
 * consume it via useReviewsDailyState():
 *
 *   ReviewsDailyAssignmentsView.vue  — Orchestrator: progress alerts, loading spinner,
 *                                       view toggle, dialogs (upload, coverage), snackbar.
 *                                       Switches between CardView and TableView.
 *
 *   ReviewsAssignmentsCardView.vue   — Card/grid layout: one card per review, grouped
 *                                       by location (chef) or user+location (admin).
 *                                       Action buttons (copy, upload) in card header.
 *
 *   ReviewsAssignmentsTableView.vue  — Table layout: one row per combo with reviews
 *                                       inline, grouped in location cards (chef) or
 *                                       user+location cards (admin).
 *
 * All three import this composable directly — no props or events needed.
 * The singleton pattern (refs declared outside the function) ensures all consumers
 * share the same reactive state instance.
 *
 * === DATA FLOW (simplified with review_assignments table) ===
 *
 * 1. On mount, orchestrator calls fetchAssignments()
 * 2. fetchAssignments() hits GET /api/reviews/assignments (+ users list for admin)
 * 3. Response populates either assignmentsData (chef) or adminData (admin)
 * 4. Computeds (userLocationGroups / adminLocationBlocks) transform raw data into
 *    grouped structures that the view components iterate over
 * 5. User actions (copy, upload) call composable methods which update state and
 *    optionally re-fetch assignments
 *
 * === COMPLETION STATE ===
 *
 * A review assignment is "completed" when status === 'completed' (set by the backend
 * when a screenshot is uploaded). Completed reviews show a "Completed" chip, reduced
 * opacity, and disabled copy/upload buttons.
 */
import { ref, computed } from "vue";
import api from "@/utils/api.js";
import { useApp } from "@/composables/useApp";
import { usePlatformColors } from "@/composables/usePlatformColors.js";
import { useUserPreferences } from "@/composables/useUserPreferences.js";

// Client's local date as YYYY-MM-DD — sent to the backend so it uses the
// correct day regardless of the server's timezone.
const clientDate = new Date().toLocaleDateString("en-CA"); // "YYYY-MM-DD" in local tz

// Human-readable formatted date for display in alerts (e.g. "28 Feb 2026")
const clientDateFormatted = new Date().toLocaleDateString(undefined, {
  day: "numeric", month: "short", year: "numeric",
});

// === Singleton state ===
// Declared outside the function so all consumers share the same refs.

const loading = ref(false);                   // True while fetchAssignments is in flight
const hasLoaded = ref(false);                 // True after first successful fetch
const assignmentsData = ref(null);            // Chef response: { assignments[], progress }
const adminData = ref(null);                  // Admin response: { users[{ userId, assignments[], progress }] }
const usersMap = ref(new Map());              // userId → { name, email } — built from /api/admin/users
const resettingReviewId = ref(null);          // ID of review being reset (shows loading spinner)
const copiedReviewId = ref(null);             // ID of review just copied (shows checkmark for 2s)

// Snackbar feedback state
const snackbar = ref({ show: false, message: "", color: "" });

// Screenshot upload dialog state — controlled by openUpload() / onUploadCompleted()
const completionDialogOpen = ref(false);
const selectedEntry = ref(null);              // Assignment entry passed to ReviewCompletionDialog
const selectedReview = ref(null);             // Review passed to ReviewCompletionDialog

// View mode preference — "cards" or "list", persisted to server-backed preferences
const { usePreference } = useUserPreferences();
const viewMode = usePreference("reviewsAssignmentsViewMode", null);

// Edit/duplicate dialog state — shared across card and table views
const editDialogOpen = ref(false);
const editDialogMode = ref("add");       // "add" | "edit" | "duplicate"
const editDialogEntry = ref(null);        // Entry object passed to ReviewScheduleEditDialog

export function useReviewsDailyState() {
  const { isAdmin } = useApp();
  const { getPlatformGradient } = usePlatformColors();

  // Current logged-in user (read from localStorage, set during login)
  const currentUser = computed(() => JSON.parse(localStorage.getItem("user") || "{}"));
  const currentUserId = computed(() => currentUser.value.id || null);

  /** Show a snackbar notification */
  function showSnack(message, color = "success") {
    snackbar.value = { show: true, message, color };
  }

  // === Helpers ===

  /**
   * Format user display name with location for admin section headers.
   * Falls back to "User #ID" if the user isn't in the usersMap.
   */
  const getUserName = (userId, location) => {
    const user = usersMap.value.get(userId);
    const name = user?.name || `User #${userId}`;
    return `${name} (${location})`;
  };

  /** Get user email from the usersMap (admin view only) */
  const getUserEmail = (userId) => {
    return usersMap.value.get(userId)?.email || "";
  };

  /** Get the display name of the user who completed a review */
  const getCopierName = (userId) => {
    if (!userId) return "";
    const user = usersMap.value.get(userId);
    return user?.name || `User #${userId}`;
  };

  /**
   * Check if a review assignment has been completed (screenshot uploaded).
   * Uses the status field from the review_assignments table.
   */
  const isReviewCompleted = (review) => {
    return review.status === "completed";
  };

  // === Computeds ===

  /**
   * Group chef's assignments by location for section headers.
   * Input:  assignmentsData.assignments = flat array of combo objects
   * Output: [{ location: "Arjan", assignments: [combo, combo, ...] }, ...]
   * Sorted alphabetically by location name.
   */
  const userLocationGroups = computed(() => {
    if (!assignmentsData.value?.assignments) return [];
    const map = new Map();
    for (const combo of assignmentsData.value.assignments) {
      if (!map.has(combo.location)) map.set(combo.location, []);
      map.get(combo.location).push(combo);
    }
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([location, assignments]) => ({ location, assignments }));
  });

  /**
   * Flatten admin data into user+location blocks for section headers.
   * Input:  adminData.users = [{ userId, assignments: [combo, ...] }, ...]
   * Output: [{ userId, location, assignments, progress: { total, copied, remaining } }, ...]
   */
  const adminLocationBlocks = computed(() => {
    if (!adminData.value?.users) return [];
    const blocks = [];
    for (const userBlock of adminData.value.users) {
      // Sub-group this user's combos by location
      const locMap = new Map();
      for (const combo of userBlock.assignments) {
        if (!locMap.has(combo.location)) locMap.set(combo.location, []);
        locMap.get(combo.location).push(combo);
      }
      // Create one block per location, sorted alphabetically
      for (const [location, assignments] of [...locMap.entries()].sort(([a], [b]) =>
        a.localeCompare(b),
      )) {
        // Calculate progress: count completed reviews
        const total = assignments.reduce((sum, c) => sum + (c.poolEmpty ? 0 : c.reviews.length), 0);
        const copied = assignments.reduce(
          (sum, c) => sum + c.reviews.filter((r) => isReviewCompleted(r)).length, 0,
        );
        blocks.push({
          userId: userBlock.userId,
          location,
          assignments,
          progress: { total, copied, remaining: total - copied },
        });
      }
    }
    return blocks;
  });

  /**
   * Overall admin progress: sum across all users.
   * Shown in the top alert bar: "X of Y completed today across Z users"
   */
  const adminOverallProgress = computed(() => {
    if (!adminData.value?.users) return { total: 0, copied: 0, remaining: 0 };
    return adminData.value.users.reduce(
      (acc, u) => ({
        total: acc.total + u.progress.total,
        copied: acc.copied + u.progress.copied,
        remaining: acc.remaining + u.progress.remaining,
      }),
      { total: 0, copied: 0, remaining: 0 },
    );
  });

  /**
   * Resolved view mode with role-based defaults.
   * If the user has a stored preference, use it.
   * Otherwise: "cards" for chefs (mobile-friendly), "list" for admins (dense overview).
   */
  const resolvedViewMode = computed(() => {
    if (viewMode.value) return viewMode.value;
    return isAdmin.value ? "list" : "cards";
  });

  // === Actions ===

  /**
   * Fetch today's assignments from the backend.
   * Makes parallel requests:
   * - GET /api/reviews/assignments (main data — from review_assignments table)
   * - GET /api/admin/users (admin only — needed for user name/email display)
   * - GET /api/accounting/sales/options (platform options for display)
   */
  const fetchAssignments = async () => {
    loading.value = true;
    try {
      const requests = [api.marketingApi.get("/api/marketing/reviews/assignments", { params: { date: clientDate } })];

      // Admins need the users list to display names/emails in section headers
      if (isAdmin.value) {
        requests.push(api.authApi.get("/api/admin/users"), api.accountingApi.get("/api/accounting/sales/options"));
      } else {
        requests.push(api.accountingApi.get("/api/accounting/sales/options"));
      }

      const results = await Promise.all(requests);
      const data = results[0].data;

      if (isAdmin.value) {
        const users = results[1].data || [];

        // Build userId → { name, email } map for display helpers
        const map = new Map();
        for (const u of users) {
          const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
          map.set(u.id, { name: name || u.email, email: u.email });
        }
        usersMap.value = map;

        adminData.value = data;       // { users[{ userId, assignments[], progress }] }
        assignmentsData.value = null; // Clear chef data (role may have changed)
      } else {
        assignmentsData.value = data; // { assignments[], progress }
        adminData.value = null;       // Clear admin data
      }

      hasLoaded.value = true;
    } catch (err) {
      console.error("Failed to load assignments:", err);
      showSnack("Failed to load assignments", "error");
    } finally {
      loading.value = false;
    }
  };

  /**
   * Copy review text to the clipboard and mark the assignment as copied.
   * Calls POST /api/reviews/assignments/:assignmentId/copy to update copied_at
   * and record cooldown. Shows a brief checkmark animation (2s).
   */
  const copyReview = async (review) => {
    try {
      await navigator.clipboard.writeText(review.text);

      // Show checkmark on the button for 2 seconds
      copiedReviewId.value = review.id;
      setTimeout(() => { copiedReviewId.value = null; }, 2000);

      // Mark the assignment as copied on the backend
      if (review.assignmentId) {
        await api.marketingApi.post(`/api/marketing/reviews/assignments/${review.assignmentId}/copy`);
      }

      showSnack("Review copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy text:", err);
      showSnack("Failed to copy to clipboard", "error");
    }
  };

  /**
   * Open the screenshot upload dialog for a specific review assignment.
   * Passes the assignment info to ReviewCompletionDialog.
   */
  function openUpload(combo, review = null) {
    selectedEntry.value = {
      assignmentId: review?.assignmentId || null,
      platform: combo.platform,
      location: combo.location,
      brand: combo.brand,
    };
    selectedReview.value = review;
    completionDialogOpen.value = true;
  }

  /**
   * Callback after successful screenshot upload.
   * Closes the dialog, shows success message, and re-fetches assignments.
   */
  async function onUploadCompleted() {
    completionDialogOpen.value = false;
    showSnack("Screenshot uploaded");
    await fetchAssignments();
  }

  /**
   * Admin action: reset an assignment back to pending state.
   * Clears copied_at, completed_at, asset_id, and resets status.
   */
  const resetReviewCopy = async (review) => {
    resettingReviewId.value = review.id;
    try {
      await api.marketingApi.post(`/api/marketing/reviews/admin/reset-assignment/${review.assignmentId}`);
      showSnack("Assignment reset — refreshing", "success");
      await fetchAssignments();
    } catch (err) {
      console.error("Failed to reset assignment:", err);
      showSnack("Failed to reset assignment", "error");
    } finally {
      resettingReviewId.value = null;
    }
  };

  /**
   * Open the edit/duplicate dialog for an assignment combo.
   * Constructs the entry object expected by ReviewScheduleEditDialog from combo data.
   * @param {Object} combo  — { platform, location, brand, num_reviews, reviews[] }
   * @param {Number} userId — the assigned user's ID (from the admin block)
   * @param {String} mode   — "edit" or "duplicate"
   */
  function openEditDialog(combo, userId, mode) {
    // Collect ALL assignment IDs for this combo so edit mode can delete them all
    const allIds = (combo.reviews || []).map((r) => r.assignmentId).filter(Boolean);
    editDialogEntry.value = {
      id: allIds[0] || null,
      allIds,
      platform: combo.platform,
      location: combo.location,
      brand: combo.brand,
      user_id: userId,
      assignment_date: clientDate,
      num_reviews: combo.num_reviews || combo.reviews?.length || 1,
    };
    editDialogMode.value = mode;
    editDialogOpen.value = true;
  }

  /**
   * Delete all assignments for a combo after user confirmation.
   * Collects all assignmentIds from combo.reviews and calls bulk-delete.
   */
  async function deleteCombo(combo) {
    // Collect all assignment IDs for this combo
    const ids = (combo.reviews || [])
      .map((r) => r.assignmentId)
      .filter(Boolean);
    if (ids.length === 0) {
      showSnack("No assignments to delete", "warning");
      return;
    }

    try {
      await api.marketingApi.post("/api/marketing/reviews/admin/assignments/bulk-delete", { ids });
      showSnack("Assignments deleted", "success");
      await fetchAssignments();
    } catch (err) {
      console.error("Failed to delete assignments:", err);
      showSnack("Failed to delete assignments", "error");
    }
  }

  /**
   * Toggle between card and list view modes.
   * Persists the new preference to server via usePreference auto-sync.
   */
  function toggleViewMode() {
    const newMode = resolvedViewMode.value === "cards" ? "list" : "cards";
    viewMode.value = newMode;
  }

  return {
    // Client date (for display in alerts)
    clientDate,
    clientDateFormatted,
    // State refs (shared singleton)
    loading,
    hasLoaded,
    assignmentsData,
    adminData,
    usersMap,
    resettingReviewId,
    copiedReviewId,
    snackbar,
    completionDialogOpen,
    selectedEntry,
    selectedReview,
    viewMode: resolvedViewMode,
    // Edit dialog state
    editDialogOpen,
    editDialogMode,
    editDialogEntry,
    // Computeds
    isAdmin,
    currentUserId,
    userLocationGroups,
    adminLocationBlocks,
    adminOverallProgress,
    // Helpers
    getPlatformGradient,
    getUserName,
    getUserEmail,
    getCopierName,
    isReviewCompleted,
    // Actions
    showSnack,
    fetchAssignments,
    copyReview,
    openUpload,
    onUploadCompleted,
    resetReviewCopy,
    toggleViewMode,
    openEditDialog,
    deleteCombo,
  };
}
